import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  Calendar as CalendarIcon,
  CheckCircle,
  XCircle,
  ChartPieSlice,
  CurrencyCircleDollar,
  TrendUp,
  TrendDown,
  Warning,
  ArrowsClockwise,
  Trophy,
  UserCircle,
  UsersThree,
  Eye,
  EyeSlash,
  ClockCountdown,
  SlidersHorizontal,
  FileText,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectTherapistId,
  selectIsAdmin,
  selectHasPermission,
  selectCanAccessFinance,
} from "../../store/slices/authSlice";
import { useGetMonthlyReportQuery } from "../../store/services/monthlyReportsApi";
import { useGetAppointmentsQuery } from "../../store/services/calendarApi";
import { useGetRoomsQuery } from "../../store/services/roomsApi";
import { getIsraelNowYearMonth } from "./reports/ReportsMonthNav";
import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHOD_COLORS,
} from "../../constants/paymentMethods";
import ContentLoader from "../shared/ContentLoader";
import "../../pages/dashboard/MonthlyReportPage.css";

const PIE_COLORS = [
  "#4dd9b4",
  "#f59e0b",
  "#60a5fa",
  "#a78bfa",
  "#f472b6",
  "#34d399",
  "#fbbf24",
  "#fb7185",
];

const HEBREW_DAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

// Arrivals-list optional columns (persisted per browser in localStorage)
const ARRIVALS_COLS_KEY = "ck_reports_arrivals_cols_v1";
const ARRIVAL_COL_DEFS = [
  { key: "time", label: "שעה" },
  { key: "type", label: "סוג טיפול" },
  { key: "room", label: "חדר" },
  { key: "payment", label: "סטטוס תשלום", financeOnly: true },
  { key: "report", label: "דוח טיפול" },
];
const DEFAULT_ARRIVAL_COLS = { time: true, type: true, room: true, payment: true, report: true };

const loadArrivalCols = () => {
  try {
    const raw = localStorage.getItem(ARRIVALS_COLS_KEY);
    if (!raw) return DEFAULT_ARRIVAL_COLS;
    return { ...DEFAULT_ARRIVAL_COLS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_ARRIVAL_COLS;
  }
};

// Legacy appointment paymentStatus → display chip
const PAYMENT_STATUS_CHIPS = {
  paid: { label: "שולם", tone: "approved" },
  pending: { label: "ממתין לתשלום", tone: "pending" },
  partial: { label: "שולם חלקית", tone: "pending" },
  debt: { label: "חוב", tone: "danger" },
  skipped: { label: "ללא חיוב", tone: "muted" },
};

export const formatHebrewMonthLong = (year, month) => {
  if (!year || !month) return "";
  return new Date(Date.UTC(year, month - 1, 1, 12)).toLocaleDateString("he-IL", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const formatCurrency = (n) => `₪${Number(n || 0).toLocaleString("he-IL")}`;

const timestampToMs = (ts) => {
  if (!ts) return 0;
  return ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime();
};

const israelMonthKeyOf = (ts) => {
  const ms = timestampToMs(ts);
  if (!ms) return null;
  return new Date(ms)
    .toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" })
    .slice(0, 7);
};

const formatTimestampDate = (ts) => {
  if (!ts) return "";
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Jerusalem",
  });
};

const formatTimestampTime = (ts) => {
  if (!ts) return "";
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jerusalem",
  });
};

const formatTimestampDateTime = (ts) => {
  if (!ts) return "";
  return `${formatTimestampDate(ts)} ${formatTimestampTime(ts)}`;
};

export { formatTimestampDateTime };

const MoMChip = ({ label, value }) => {
  if (value === null || value === undefined) {
    return (
      <div className="mr-mom-chip" title="אין נתוני חודש קודם">
        <span className="mr-mom-chip-label">{label}</span>
        <span className="mr-mom-chip-value">—</span>
      </div>
    );
  }
  const positive = value >= 0;
  return (
    <div className={`mr-mom-chip ${positive ? "mr-mom-up" : "mr-mom-down"}`}>
      <span className="mr-mom-chip-label">{label}</span>
      <span className="mr-mom-chip-value">
        {positive ? <TrendUp size={14} /> : <TrendDown size={14} />}
        {Math.abs(value)}%
      </span>
    </div>
  );
};

const MetricCard = ({ icon, label, value, tone = "default" }) => (
  <div className={`mr-metric-card mr-metric-${tone}`}>
    <div className="mr-metric-icon">{icon}</div>
    <div className="mr-metric-body">
      <div className="mr-metric-label">{label}</div>
      <div className="mr-metric-value">{value}</div>
    </div>
  </div>
);

const ALL_SECTIONS = ["meetings", "finance", "growth"];

/**
 * Renders the full monthly report body (meeting analytics, finance analytics,
 * growth/engagement) plus the per-therapist scope selector for a given month.
 * The page-level header (title / navigation / back button) is supplied by the
 * wrapping page (MonthlyReportPage or MonthlyReportsPage).
 *
 * Props:
 * - sections: which report sections to render ("meetings" | "finance" |
 *   "growth"). Defaults to all — backward compatible.
 * - scopeTherapistId: when provided (including null = whole clinic) the scope
 *   is CONTROLLED by the parent and the internal selector is hidden.
 * - afterMeetingsChartsSlot: optional node rendered inside the meetings
 *   section, between the charts and the meetings list (12-month trend etc.).
 */
const MonthlyReportContent = ({
  monthKey,
  sections = ALL_SECTIONS,
  scopeTherapistId: controlledScope,
  afterMeetingsChartsSlot = null,
}) => {
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const isAdmin = useSelector(selectIsAdmin);
  // Permissions v2: clinic-wide report scope (admin, or reports.viewClinicWide)
  const canViewClinicWideReports = useSelector(
    selectHasPermission("reports.viewClinicWide")
  );
  const hasClinicWideScope = isAdmin || canViewClinicWideReports;
  const canAccessFinance = useSelector(selectCanAccessFinance);

  const { data: report, isLoading, error, refetch } = useGetMonthlyReportQuery(
    { clinicId, monthKey, clinicWide: hasClinicWideScope },
    { skip: !clinicId || !monthKey }
  );

  const isSoloClinic = report?.meta?.isSoloClinic === true;
  const therapistOptions = useMemo(() => {
    if (!report?.perTherapist) return [];
    return Object.entries(report.perTherapist).map(([id, data]) => ({
      id,
      name: data.therapistName || "מטפל/ת",
      inactive: data.inactive === true,
    }));
  }, [report]);

  // Non-admins are pinned to their own slice in non-solo clinics.
  const [scopeTherapistId, setScopeTherapistId] = useState(null);
  // Arrivals list: amounts are masked by default (screen-sharing safety).
  const [showAmounts, setShowAmounts] = useState(false);
  const [arrivalCols, setArrivalCols] = useState(loadArrivalCols);
  const [colsPanelOpen, setColsPanelOpen] = useState(false);

  const toggleArrivalCol = (key) => {
    setArrivalCols((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(ARRIVALS_COLS_KEY, JSON.stringify(next));
      } catch {
        // localStorage unavailable (private mode) — state still works in-session
      }
      return next;
    });
  };
  const isScopeControlled = controlledScope !== undefined;
  const effectiveScope = useMemo(() => {
    if (isScopeControlled) return controlledScope || null;
    if (isSoloClinic) return null;
    if (!hasClinicWideScope) return therapistId || null;
    return scopeTherapistId;
  }, [
    isScopeControlled,
    controlledScope,
    isSoloClinic,
    hasClinicWideScope,
    therapistId,
    scopeTherapistId,
  ]);

  const slice = useMemo(() => {
    if (!report) return null;
    if (!effectiveScope) {
      return {
        meetingAnalytics: report.meetingAnalytics,
        financeAnalytics: report.financeAnalytics,
        growthInsights: report.growthInsights,
        meetingsAll: report.meetingAnalytics?.meetings || [],
        outstandingAll: report.financeAnalytics?.outstanding || [],
      };
    }
    const perT = report.perTherapist?.[effectiveScope];
    if (!perT) {
      return {
        meetingAnalytics: null,
        financeAnalytics: null,
        growthInsights: null,
        meetingsAll: [],
        outstandingAll: [],
      };
    }
    // Derive per-therapist meetings/outstanding from clinic-wide arrays
    const meetingsAll = (report.meetingAnalytics?.meetings || []).filter(
      (m) => m.therapistId === effectiveScope
    );
    const outstandingAll = (report.financeAnalytics?.outstanding || []).filter(
      (o) => o.therapistId === effectiveScope
    );
    return {
      meetingAnalytics: perT.meetingAnalytics,
      financeAnalytics: perT.financeAnalytics,
      growthInsights: perT.growthInsights,
      meetingsAll,
      outstandingAll,
    };
  }, [report, effectiveScope]);

  // Appointments-per-day for the month (line chart). Schema v3 docs carry a
  // server-computed, uncapped dailyMeetings[] — prefer it; fall back to the
  // meetings[]-derived counts for older (v2) docs.
  const dailyMeetingsData = useMemo(() => {
    if (!monthKey) return [];
    const [y, m] = monthKey.split("-").map(Number);
    if (!y || !m) return [];
    const daysInMonth = new Date(y, m, 0).getDate();
    const counts = new Array(daysInMonth + 1).fill(0);

    const serverDaily = slice?.meetingAnalytics?.dailyMeetings;
    if (Array.isArray(serverDaily) && serverDaily.length > 0) {
      for (const entry of serverDaily) {
        const day = Number(entry?.dateISO?.slice(8, 10));
        if (day >= 1 && day <= daysInMonth) counts[day] += entry.count || 0;
      }
    } else {
      const meetings = slice?.meetingsAll;
      if (!meetings || meetings.length === 0) return [];
      for (const mt of meetings) {
        const ts = mt.startTime;
        if (!ts) continue;
        const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
        const day = Number(
          d.toLocaleDateString("en-US", {
            day: "numeric",
            timeZone: "Asia/Jerusalem",
          })
        );
        if (day >= 1 && day <= daysInMonth) counts[day] += 1;
      }
    }
    return Array.from({ length: daysInMonth }, (_, i) => ({
      name: String(i + 1),
      appointments: counts[i + 1],
    }));
  }, [monthKey, slice]);

  // ── Live overlay for the arrivals list ──
  // The report doc is recomputed by a 60s-debounced Cloud Task, so right after
  // the therapist updates a meeting in the pending-actions flow the doc lags.
  // For the current + previous month (the window the pending flow touches) we
  // fetch the live appointment docs and merge them over the report rows, so
  // approval / payment / treatment-report statuses are always fresh. RTK tag
  // invalidation (Appointments LIST) refetches this query after every
  // appointment or finance mutation in the session.
  const isRecentMonth = useMemo(() => {
    if (!monthKey) return false;
    const now = getIsraelNowYearMonth();
    const cur = `${now.year}-${String(now.month).padStart(2, "0")}`;
    const prev =
      now.month === 1
        ? `${now.year - 1}-12`
        : `${now.year}-${String(now.month - 1).padStart(2, "0")}`;
    return monthKey === cur || monthKey === prev;
  }, [monthKey]);

  const monthRangeArgs = useMemo(() => {
    if (!monthKey) return null;
    const [y, m] = monthKey.split("-").map(Number);
    if (!y || !m) return null;
    // Browser-local month bounds; Israel users match Asia/Jerusalem. Rows are
    // still month-guarded via israelMonthKeyOf before being appended.
    return {
      startDate: new Date(y, m - 1, 1).toISOString(),
      endDate: new Date(y, m, 0, 23, 59, 59, 999).toISOString(),
    };
  }, [monthKey]);

  const { data: liveAppointments } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: monthRangeArgs?.startDate,
      endDate: monthRangeArgs?.endDate,
      therapistId: effectiveScope || null,
    },
    { skip: !clinicId || !isRecentMonth || !monthRangeArgs }
  );

  const { data: rooms } = useGetRoomsQuery(
    { clinicId },
    { skip: !clinicId || !isRecentMonth }
  );

  const roomNameById = useMemo(() => {
    const map = new Map();
    for (const r of rooms || []) if (r?.name) map.set(r.id, r.name);
    return map;
  }, [rooms]);

  const liveById = useMemo(() => {
    const map = new Map();
    for (const a of liveAppointments || []) map.set(a.id, a);
    return map;
  }, [liveAppointments]);

  // Meetings that actually took place this month (Cloud Task marked them
  // completed at endTime; therapist-declared no-shows become cancelled and
  // drop out). Report rows merged with live docs where available; live-only
  // completed meetings (recompute still pending) are appended. Oldest first.
  const arrivedMeetings = useMemo(() => {
    const meetings = slice?.meetingsAll || [];
    const rows = [];
    const seen = new Set();

    for (const m of meetings) {
      const live = liveById.get(m.appointmentId);
      if (live) {
        // Live doc is authoritative: drops cancelled/deleted, refreshes flags.
        if (live.isDeleted === true || live.status !== "completed") continue;
        seen.add(m.appointmentId);
        rows.push({
          ...m,
          startTime: live.startTime ?? m.startTime,
          treatmentTypeName: live.treatmentTypeName ?? m.treatmentTypeName,
          paymentAmount:
            typeof live.paymentAmount === "number"
              ? live.paymentAmount
              : m.paymentAmount ?? null,
          paymentStatus: live.paymentStatus ?? m.paymentStatus ?? null,
          isApprovedByTherapist: live.isApprovedByTherapist === true,
          isTherapistSummarizedTheMeeting:
            live.isTherapistSummarizedTheMeeting === true,
          isTempCustomer:
            live.isTempCustomer === true || m.isTempCustomer === true,
          roomName: live.roomId
            ? roomNameById.get(live.roomId) || m.roomName || null
            : null,
        });
      } else if (m.status === "completed") {
        seen.add(m.appointmentId);
        rows.push(m);
      }
    }

    // Completed meetings the report doc doesn't carry yet (60s recompute lag)
    for (const a of liveById.values()) {
      if (seen.has(a.id)) continue;
      if (a.status !== "completed") continue;
      if (a.isDeleted === true || a.isBlockedTime === true) continue;
      if (effectiveScope && a.therapistId !== effectiveScope) continue;
      if (israelMonthKeyOf(a.startTime) !== monthKey) continue;
      rows.push({
        appointmentId: a.id,
        startTime: a.startTime || null,
        patientId: a.patientId || null,
        patientName: a.patientName || "—",
        therapistId: a.therapistId || null,
        treatmentTypeName: a.treatmentTypeName || null,
        status: "completed",
        paymentAmount:
          typeof a.paymentAmount === "number" ? a.paymentAmount : null,
        paymentStatus: a.paymentStatus || null,
        isApprovedByTherapist: a.isApprovedByTherapist === true,
        isTherapistSummarizedTheMeeting:
          a.isTherapistSummarizedTheMeeting === true,
        isTempCustomer: a.isTempCustomer === true,
        roomName: a.roomId ? roomNameById.get(a.roomId) || null : null,
      });
    }

    rows.sort((a, b) => timestampToMs(a.startTime) - timestampToMs(b.startTime));
    return rows;
  }, [slice, liveById, roomNameById, effectiveScope, monthKey]);

  if (isLoading) {
    return (
      <ContentLoader fill message="טוען דוח..." />
    );
  }

  if (error) {
    return (
      <div className="mr-error">
        <h2>לא ניתן לטעון את הדוח</h2>
        <button type="button" className="mr-retry-btn" onClick={() => refetch()}>
          נסה שוב
        </button>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="mr-empty">
        <h2>הדוח החודשי טרם הופק</h2>
        <p>דוחות מופקים אוטומטית בסוף כל חודש.</p>
      </div>
    );
  }

  const { meetingAnalytics, financeAnalytics, growthInsights, outstandingAll } =
    slice || {};

  const showFinance = canAccessFinance !== false && canAccessFinance !== null;
  const showScopeSelector =
    !isScopeControlled && hasClinicWideScope && !isSoloClinic && therapistOptions.length > 0;
  const treatmentTypes = meetingAnalytics?.perTreatmentType || [];
  const showTreatmentPie = treatmentTypes.length > 1;

  return (
    <>
      {showScopeSelector && (
        <div className="mr-scope-row">
          <select
            className="mr-scope-select"
            value={scopeTherapistId || ""}
            onChange={(e) => setScopeTherapistId(e.target.value || null)}
            aria-label="בחר מטפל/ת"
          >
            <option value="">כל המטפלים</option>
            {therapistOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.inactive ? " (לא פעיל)" : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Meeting Analytics */}
      {sections.includes("meetings") && (
      <section className="mr-section">
        <h2 className="mr-section-title">
          <ChartPieSlice size={18} weight="fill" />
          ניתוח פגישות
        </h2>

        {!meetingAnalytics ? (
          <div className="mr-empty-section">אין נתוני פגישות לחודש זה</div>
        ) : (
          <>
            <div className="mr-metrics-grid">
              <MetricCard
                icon={<CalendarIcon size={20} />}
                label="פגישות מתוכננות"
                value={meetingAnalytics.totalScheduled || 0}
              />
              <MetricCard
                icon={<CheckCircle size={20} weight="fill" />}
                label="פגישות שהושלמו"
                value={meetingAnalytics.totalCompleted || 0}
                tone="positive"
              />
              <MetricCard
                icon={<XCircle size={20} weight="fill" />}
                label="פגישות שבוטלו"
                value={meetingAnalytics.totalCancelled || 0}
                tone="negative"
              />
              <MetricCard
                icon={<TrendUp size={20} />}
                label="אחוז השלמה"
                value={`${meetingAnalytics.completionRate || 0}%`}
              />
            </div>

            {meetingAnalytics.busiestDay && (
              <div className="mr-busiest-banner">
                <strong>היום העמוס ביותר:</strong>{" "}
                יום {HEBREW_DAYS[meetingAnalytics.busiestDay.dayOfWeek]}{" "}
                ({meetingAnalytics.busiestDay.dateISO}) —{" "}
                {meetingAnalytics.busiestDay.count} פגישות
              </div>
            )}

            {dailyMeetingsData.length > 0 && (
              <div className="mr-chart-card">
                <h3>פגישות החודש</h3>
                <div className="mr-chart-wrap">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart
                      data={dailyMeetingsData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(0,0,0,0.06)"
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 10, fill: "#6b7280" }}
                        tickLine={false}
                        axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                        interval={
                          dailyMeetingsData.length > 15
                            ? Math.floor(dailyMeetingsData.length / 10)
                            : 0
                        }
                        height={24}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#6b7280" }}
                        tickLine={false}
                        axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        formatter={(value) => [value, "פגישות"]}
                        labelFormatter={(label) => `יום ${label}`}
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: 6,
                          fontSize: 12,
                          direction: "rtl",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="appointments"
                        stroke="#4dd9b4"
                        strokeWidth={2}
                        dot={{ fill: "#4dd9b4", strokeWidth: 0, r: 3 }}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {showTreatmentPie && (
              <div className="mr-chart-card">
                <h3>חלוקה לפי סוג טיפול</h3>
                <div className="mr-chart-wrap">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={treatmentTypes}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {treatmentTypes.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => `${value} פגישות`}
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: 6,
                          fontSize: 12,
                          direction: "rtl",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mr-legend">
                  {treatmentTypes.map((t, i) => (
                    <div key={t.treatmentTypeId || `tt-${i}`} className="mr-legend-item">
                      <span
                        className="mr-legend-dot"
                        style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                      />
                      <span className="mr-legend-name">{t.name}</span>
                      <span className="mr-legend-count">
                        {t.count} ({t.percent}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {meetingAnalytics.perPatient && meetingAnalytics.perPatient.length > 0 && (
              <div className="mr-chart-card">
                <h3>פגישות לפי מטופלים</h3>
                <div className="mr-chart-with-table">
                  <div className="mr-chart-wrap">
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={meetingAnalytics.perPatient.slice(0, 8)}
                          dataKey="count"
                          nameKey="patientName"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={90}
                          paddingAngle={2}
                          stroke="none"
                        >
                          {meetingAnalytics.perPatient.slice(0, 8).map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => `${value} פגישות`}
                          contentStyle={{
                            background: "#fff",
                            border: "1px solid rgba(0,0,0,0.08)",
                            borderRadius: 6,
                            fontSize: 12,
                            direction: "rtl",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mr-per-patient-list">
                    {meetingAnalytics.perPatient.map((p, i) => (
                      <div className="mr-list-item" key={p.patientId || `pp-${i}`}>
                        <span className="mr-list-cell mr-list-name">
                          {p.patientName || "—"}
                        </span>
                        <span className="mr-list-cell mr-per-patient-count">
                          {p.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {afterMeetingsChartsSlot}

            {arrivedMeetings.length > 0 && (
              <div className="mr-list-card">
                <div className="mr-arrivals-head">
                  <h3>
                    <UsersThree size={16} weight="fill" />
                    מטופלים שהגיעו החודש ({arrivedMeetings.length})
                  </h3>
                  <div className="mr-arrivals-actions">
                    {showFinance && (
                      <button
                        type="button"
                        className="mr-amount-toggle"
                        onClick={() => setShowAmounts((v) => !v)}
                      >
                        {showAmounts ? <EyeSlash size={14} /> : <Eye size={14} />}
                        {showAmounts ? "הסתר סכומים" : "הצג סכומים"}
                      </button>
                    )}
                    <button
                      type="button"
                      className={`mr-amount-toggle ${colsPanelOpen ? "mr-toggle-active" : ""}`}
                      onClick={() => setColsPanelOpen((v) => !v)}
                    >
                      <SlidersHorizontal size={14} />
                      תצוגה
                    </button>
                  </div>
                </div>
                {colsPanelOpen && (
                  <div className="mr-cols-row">
                    {ARRIVAL_COL_DEFS.filter(
                      (c) => !c.financeOnly || showFinance
                    ).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        className={`mr-col-pill ${arrivalCols[c.key] ? "mr-col-pill--on" : ""}`}
                        onClick={() => toggleArrivalCol(c.key)}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
                <p className="mr-note">
                  כל הפגישות שהסתיימו החודש, לפי תאריך. אושרה — המטפל/ת אישר/ה
                  שהפגישה התקיימה (בסעיף הפעולות הממתינות במסך הבית). ממתינה
                  לאישור — הפגישה טרם עודכנה שם. בכפתור התצוגה ניתן לבחור אילו
                  עמודות להציג — הבחירה נשמרת בדפדפן.
                </p>
                <div className="mr-list">
                  {arrivedMeetings.slice(0, 200).map((m) => (
                    <div
                      className="mr-list-item mr-arrivals-item"
                      key={m.appointmentId}
                    >
                      <span className="mr-list-cell mr-list-name">
                        {m.patientName}
                      </span>
                      <span className="mr-list-cell mr-list-date">
                        {formatTimestampDate(m.startTime)}
                      </span>
                      {arrivalCols.time && (
                        <span className="mr-list-cell mr-list-time">
                          {formatTimestampTime(m.startTime)}
                        </span>
                      )}
                      {arrivalCols.type && (
                        <span className="mr-list-cell mr-list-tt">
                          {m.treatmentTypeName || "—"}
                        </span>
                      )}
                      {arrivalCols.room && m.roomName && (
                        <span className="mr-list-cell mr-list-room">
                          {m.roomName}
                        </span>
                      )}
                      {showFinance && (
                        <span className="mr-list-cell mr-list-amount">
                          {showAmounts
                            ? m.paymentAmount != null
                              ? formatCurrency(m.paymentAmount)
                              : "—"
                            : "₪•••"}
                        </span>
                      )}
                      {arrivalCols.payment &&
                        showFinance &&
                        PAYMENT_STATUS_CHIPS[m.paymentStatus] && (
                          <span
                            className={`mr-status-chip mr-status-chip--${
                              PAYMENT_STATUS_CHIPS[m.paymentStatus].tone
                            }`}
                          >
                            {PAYMENT_STATUS_CHIPS[m.paymentStatus].label}
                          </span>
                        )}
                      {arrivalCols.report &&
                        m.isTempCustomer !== true &&
                        typeof m.isTherapistSummarizedTheMeeting ===
                          "boolean" && (
                          <span
                            className={`mr-status-chip ${
                              m.isTherapistSummarizedTheMeeting
                                ? "mr-status-chip--approved"
                                : "mr-status-chip--muted"
                            }`}
                          >
                            <FileText size={12} weight="fill" />
                            {m.isTherapistSummarizedTheMeeting
                              ? "דוח הוזן"
                              : "חסר דוח"}
                          </span>
                        )}
                      {typeof m.isApprovedByTherapist === "boolean" && (
                        <span
                          className={`mr-status-chip ${
                            m.isApprovedByTherapist
                              ? "mr-status-chip--approved"
                              : "mr-status-chip--pending"
                          }`}
                        >
                          {m.isApprovedByTherapist ? (
                            <CheckCircle size={12} weight="fill" />
                          ) : (
                            <ClockCountdown size={12} weight="fill" />
                          )}
                          {m.isApprovedByTherapist ? "אושרה" : "ממתינה לאישור"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {arrivedMeetings.length > 200 && (
                  <div className="mr-list-truncated">
                    מוצגות 200 פגישות מתוך {arrivedMeetings.length}
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </section>
      )}

      {/* Finance Analytics */}
      {sections.includes("finance") && showFinance && financeAnalytics && (
        <section className="mr-section">
          <h2 className="mr-section-title">
            <CurrencyCircleDollar size={18} weight="fill" />
            ניתוח פיננסי
          </h2>

          <div className="mr-metrics-grid">
            <MetricCard
              icon={<CurrencyCircleDollar size={20} weight="fill" />}
              label="נגבה החודש"
              value={formatCurrency(financeAnalytics.collectedTotal)}
              tone="positive"
            />
            <MetricCard
              icon={<CurrencyCircleDollar size={20} />}
              label="צפוי להתקבל"
              value={formatCurrency(financeAnalytics.expectedTotal)}
            />
            <MetricCard
              icon={<Warning size={20} />}
              label="ממתין לתשלום"
              value={formatCurrency(financeAnalytics.pendingTotal)}
              tone="warning"
            />
          </div>

          <div className="mr-bookings-split">
            <h3>ערך הזמנות חדשות החודש</h3>
            <div className="mr-split-row">
              <div className="mr-split-card">
                <div className="mr-split-label">לפגישות החודש</div>
                <div className="mr-split-value">
                  {formatCurrency(financeAnalytics.newBookingsValueThisMonth)}
                </div>
              </div>
              <div className="mr-split-card">
                <div className="mr-split-label">לחודשים הבאים</div>
                <div className="mr-split-value">
                  {formatCurrency(financeAnalytics.newBookingsValueFutureMonths)}
                </div>
              </div>
            </div>
            {financeAnalytics.missingPriceCount > 0 && (
              <div className="mr-missing-price-banner">
                <Warning size={16} weight="fill" />
                <span>
                  {financeAnalytics.missingPriceCount} פגישות ללא מחיר משויך —
                  לא נכללו בסכומים
                </span>
              </div>
            )}
          </div>

          {financeAnalytics.paymentMethodDistribution &&
            financeAnalytics.paymentMethodDistribution.length > 0 && (
              <div className="mr-chart-card">
                <h3>התפלגות הכנסות לפי אמצעי תשלום</h3>
                <div className="mr-chart-wrap">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={financeAnalytics.paymentMethodDistribution}
                        dataKey="total"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {financeAnalytics.paymentMethodDistribution.map((b) => (
                          <Cell
                            key={b.method}
                            fill={
                              PAYMENT_METHOD_COLORS[b.method] ||
                              PAYMENT_METHOD_COLORS.other
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [
                          formatCurrency(value),
                          PAYMENT_METHOD_LABELS[name] || name,
                        ]}
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: 6,
                          fontSize: 12,
                          direction: "rtl",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mr-legend">
                  {financeAnalytics.paymentMethodDistribution.map((b) => (
                    <div key={b.method} className="mr-legend-item">
                      <span
                        className="mr-legend-dot"
                        style={{
                          background:
                            PAYMENT_METHOD_COLORS[b.method] ||
                            PAYMENT_METHOD_COLORS.other,
                        }}
                      />
                      <span className="mr-legend-name">
                        {PAYMENT_METHOD_LABELS[b.method] || b.method}
                      </span>
                      <span className="mr-legend-count">
                        {formatCurrency(b.total)} ({b.count})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {outstandingAll && outstandingAll.length > 0 && (
            <div className="mr-list-card">
              <h3>תשלומים ממתינים</h3>
              <div className="mr-list">
                {outstandingAll.map((o) => (
                  <div className="mr-list-item" key={o.transactionId}>
                    <span className="mr-list-cell mr-list-name">{o.patientName}</span>
                    <span className="mr-list-cell mr-list-amount">
                      {formatCurrency(o.amount)}
                    </span>
                    <span className="mr-list-cell mr-list-date">
                      {formatTimestampDate(o.dueDate)}
                      {o.daysOverdue > 0 && (
                        <span className="mr-overdue"> ({o.daysOverdue} ימים)</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Growth & Engagement */}
      {sections.includes("growth") && growthInsights && (
        <section className="mr-section">
          <h2 className="mr-section-title">
            <TrendUp size={18} weight="fill" />
            צמיחה ומעורבות
          </h2>

          <div className="mr-mom-row">
            <MoMChip label="הכנסות vs חודש קודם" value={growthInsights.momRevenuePct} />
            <MoMChip label="פגישות vs חודש קודם" value={growthInsights.momMeetingsPct} />
          </div>

          {growthInsights.top3Patients && growthInsights.top3Patients.length > 0 && (
            <div className="mr-list-card">
              <h3>
                <Trophy size={16} weight="fill" /> 3 המטופלים הפעילים ביותר
              </h3>
              <div className="mr-list">
                {growthInsights.top3Patients.map((p, i) => (
                  <div className="mr-list-item" key={p.patientId}>
                    <span className="mr-top-rank">#{i + 1}</span>
                    <span className="mr-list-cell mr-list-name">{p.patientName}</span>
                    <span className="mr-list-cell mr-list-amount">
                      {formatCurrency(p.revenue)}
                    </span>
                    <span className="mr-list-cell mr-list-count">
                      {p.meetings} פגישות
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {growthInsights.reEngageList && growthInsights.reEngageList.length > 0 && (
            <div className="mr-list-card">
              <h3>
                <ArrowsClockwise size={16} weight="fill" /> מומלץ לקבוע פגישת המשך
              </h3>
              <div className="mr-list">
                {growthInsights.reEngageList.map((p) => (
                  <div className="mr-list-item" key={p.patientId}>
                    <UserCircle size={20} />
                    <span className="mr-list-cell mr-list-name">{p.patientName}</span>
                    <span className="mr-list-cell mr-list-count">
                      {p.meetingsThisMonth} פגישות החודש
                    </span>
                    <button
                      type="button"
                      className="mr-reengage-btn"
                      onClick={() => navigate(`/dashboard/patients/${p.patientId}`)}
                    >
                      קבע פגישה
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
};

export default MonthlyReportContent;
